import { differenceInMinutes, differenceInHours, differenceInDays, isValid, parse } from 'date-fns';

const AI_GENERATORS = ["Midjourney", "DALL-E", "Stable Diffusion", "Firefly", "Runway", "Pika", "Jasper"];
const EDITORS = ["Photoshop", "Lightroom", "Snapseed", "VSCO", "Canva", "CapCut", "PicsArt", "Facetune", "GIMP"];

export const parseExifDate = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const cleanStr = dateStr.trim().replace(/\0/g, '');
  let date = parse(cleanStr, 'yyyy:MM:dd HH:mm:ss', new Date());
  if (!isValid(date)) date = parse(cleanStr, 'yyyy:MM:dd HH:mm', new Date());
  if (!isValid(date)) date = new Date(cleanStr.replace(/:/g, (m, i) => i < 10 ? '-' : m));
  return isValid(date) ? date : null;
};

const applyOffset = (date, offsetStr) => {
  if (!date || !offsetStr) return { date, hasOffset: false };
  const match = offsetStr.match(/([+-])(\d{2}):(\d{2})/);
  if (!match) return { date, hasOffset: false };
  const sign = match[1] === '+' ? 1 : -1;
  const hours = parseInt(match[2], 10);
  const minutes = parseInt(match[3], 10);
  const totalOffsetMinutes = (hours * 60 + minutes) * sign;
  const absoluteTime = new Date(date.getTime() - (totalOffsetMinutes * 60 * 1000));
  return { date: absoluteTime, hasOffset: true, originalOffset: totalOffsetMinutes };
};

export const analyzeForensics = (file, tags) => {
  const fileModified = new Date(file.lastModified);
  const rules = [];
  let riskScore = 0;

  const rawTags = {
    DateTimeOriginal: tags['DateTimeOriginal']?.description,
    CreateDate: tags['CreateDate']?.description || tags['DateTimeDigitized']?.description,
    ModifyDate: tags['ModifyDate']?.description || tags['DateTime']?.description,
    Software: tags['Software']?.description,
    Make: tags['Make']?.description,
    Model: tags['Model']?.description,
    ImageWidth: tags['Image Width']?.value || tags['ExifImageWidth']?.value,
    ImageHeight: tags['Image Height']?.value || tags['ExifImageHeight']?.value,
  };

  const tOriginal = applyOffset(parseExifDate(rawTags.DateTimeOriginal), tags['OffsetTimeOriginal']?.description);
  
  // --- AI SCORING LOGIC ---

  // 1. Direct AI Attribution (The "Smoking Gun")
  if (rawTags.Software) {
    const sw = rawTags.Software.toLowerCase();
    const isSynthetic = AI_GENERATORS.find(g => sw.includes(g.toLowerCase()));
    const isEdited = EDITORS.find(e => sw.includes(e.toLowerCase()));

    if (isSynthetic) {
      rules.push({ code: 'AI-1', msg: `Direct AI attribution found: ${rawTags.Software}`, risk: 65, category: 'Completely AI' });
    } else if (isEdited) {
      rules.push({ code: 'AI-2', msg: `Image processed via AI-capable editor: ${rawTags.Software}`, risk: 30, category: 'Edited by AI' });
    }
  }

  // 2. Metadata Stripping Pattern (Common in AI)
  if (!rawTags.Make && !rawTags.Model && !rawTags.DateTimeOriginal) {
    rules.push({ code: 'FS-1', msg: "Total metadata wipe detected. Typical for generative AI outputs.", risk: 40, category: 'Highly AI' });
  } else if (!rawTags.Make && !rawTags.Model) {
    rules.push({ code: 'FS-2', msg: "Missing camera hardware signatures (Make/Model).", risk: 20, category: 'Highly AI' });
  }

  // 3. Temporal Anomaly (Post-Capture Modification)
  if (tOriginal.date) {
    const diffDays = differenceInDays(fileModified, tOriginal.date);
    if (diffDays > 30 && !rawTags.Software) {
      rules.push({ code: 'T-1', msg: "Large temporal gap between capture and file creation with missing software tags.", risk: 15, category: 'Edited by AI' });
    }
    if (differenceInMinutes(fileModified, tOriginal.date) < -5) {
      rules.push({ code: 'T-2', msg: "Backdated metadata detected. File system time precedes image capture.", risk: 25, category: 'Edited by AI' });
    }
  }

  // 4. Resolution Analysis
  if ((rawTags.ImageWidth === 1024 && rawTags.ImageHeight === 1024) || (rawTags.ImageWidth === 512 && rawTags.ImageHeight === 512)) {
    if (!rawTags.Make) {
      rules.push({ code: 'R-1', msg: "Resolution matches common base AI model training dimensions (1024x1024).", risk: 15, category: 'Highly AI' });
    }
  }

  riskScore = Math.min(rules.reduce((acc, r) => acc + r.risk, 0), 100);

  // Determine final label based on strict thresholds
  let finalLabel = "Human / Verifiable";
  let statusColor = "text-green-500";
  
  if (riskScore >= 75) {
    finalLabel = "Completely AI";
    statusColor = "text-red-500";
  } else if (riskScore >= 45) {
    finalLabel = "Highly AI";
    statusColor = "text-orange-500";
  } else if (riskScore >= 20) {
    finalLabel = "Edited by AI";
    statusColor = "text-yellow-500";
  } else if (riskScore > 0) {
    finalLabel = "Human / Likely Edited";
    statusColor = "text-blue-400";
  }

  return {
    timestamps: { original: tOriginal.date, fileModified: fileModified },
    rawTags,
    rules,
    scores: {
      risk: riskScore,
      veracity: 100 - riskScore,
      label: finalLabel,
      color: statusColor
    },
    summary: `NEURAL AUDIT REPORT\nClassification: ${finalLabel}\nRisk Score: ${riskScore}%\nMarkers Detected: ${rules.map(r => r.code).join(', ')}`
  };
};